import * as React from 'react';
import { Button, Container, Header, Icon, Modal as UIModal, Segment } from 'semantic-ui-react';
import { ItemsQuery } from '~/Api';
import { SystemModule } from '~/Api/Authentication/AccessToken';
import * as InfoCardApi from '~/Api/InfoCard';
import { useApi, useApiResult, useApiResults } from '~/ApiHooks';
import { useRequireAccessToken } from '~/Application/useRequireAccessToken';
import { DashboardRoute, InfoCardRoute, UserProfileRoute } from '~/Application/routes';
import { useStore } from '~/Application/Store';
import { ErrorConsumer } from '~/ErrorHandler';
import * as Impersonate from '~/Impersonate';
import { Grid, Modal } from '~/InfoCard';
import { BrandFilter, DateFilter, InfoCardTypeFilter, SourceFilter, TagsFilter } from '~/InfoCardsFilter';
import { useSearchParams } from '~/Router';
import { ErrorAlert, Placeholder } from '~/UI';
var ItemsPerPage = 50;
var selectInfoCard = function (id, pages) {
    if (id === null || pages === null) {
        return null;
    }
    return pages.reduce(function (infoCard, page) { var _a; return (_a = page.items.find(function (infoCard) { return infoCard.id === id; })) !== null && _a !== void 0 ? _a : infoCard; }, null);
};
var InfoCardModal = function (_a) {
    var id = _a.id, InfoCardsLink = _a.InfoCardsLink, results = _a.results, onClose = _a.onClose;
    var preloadedInfoCard = React.useMemo(function () { return results.map(function () { return selectInfoCard(id, results.value); }); }, [id]);
    var _b = useApi(), fetch = _b[0], result = _b[1];
    React.useEffect(function () {
        if (preloadedInfoCard.value !== null || preloadedInfoCard.isLoading) {
            return undefined;
        }
        fetch(new InfoCardApi.InfoCardRequest(id));
        return undefined;
    }, [preloadedInfoCard]);
    var infoCard = preloadedInfoCard.value !== null ? preloadedInfoCard : result;
    if (infoCard.value !== null) {
        return <Modal infoCard={infoCard.value} InfoCardsLink={InfoCardsLink} onClose={onClose}/>;
    }
    if (infoCard.isResolved) {
        return (<UIModal open onClose={onClose}>
				<UIModal.Content>
					<ErrorAlert>Aktualita nenalezene, zkontrolujte prosím zadanou adresu</ErrorAlert>
				</UIModal.Content>
				<UIModal.Actions>
					<Button size='mini' onClick={onClose}>
						Zavřít
					</Button>
				</UIModal.Actions>
			</UIModal>);
    }
    return (<UIModal open>
			<UIModal.Content>
				<Placeholder />
			</UIModal.Content>
		</UIModal>);
};
var Filter = React.memo(function (_a) {
    var allowBrandFilter = _a.allowBrandFilter, filter = _a.filter, filtersMeta = _a.filtersMeta, filterOptions = _a.filterOptions, onUpdate = _a.onUpdate;
    var extraTags = React.useMemo(function () {
        if (filtersMeta === null) {
            return [];
        }
        return filter.conditions
            .map(function (condition) {
            switch (condition.type) {
                case InfoCardApi.TagType.market:
                    return filtersMeta.market[0];
                default:
                    return null;
            }
        })
            .filter(function (tag) { return tag !== null; });
    }, [filter, filtersMeta]);
    var filteredSource = filter.conditions.find(function (tag) { return tag.type === InfoCardApi.TagType.source; });
    var filteredBrandsTags = filter.conditions.filter(function (tag) { return tag.type === InfoCardApi.TagType.brand; });
    var filteredBrandTagsValues = filteredBrandsTags.map(function (tag) { return tag.value; });
    return (<Segment.Group data-testid='filters'>
			<Segment clearing>
				<Header as='h4' className='dashboard-filter-btn' floated='left'>
					<Icon name='filter' size='small'/>
					Filtr
				</Header>
				<Header as='h4' className='dashboard-profile-btn' floated='right'>
					<UserProfileRoute.Link>
						<a>Skryté aktuality</a>
					</UserProfileRoute.Link>
				</Header>
			</Segment>
			{filtersMeta !== null ? (<BrandFilter brands={filterOptions.brands} disabled={!allowBrandFilter} filter={filter} filteredBrands={filteredBrandTagsValues}/>) : (<Segment>
					<Placeholder data-testid='loading' line={4}/>
				</Segment>)}
			{filtersMeta !== null ? (<SourceFilter data-testid='loading' filter={filter} sources={filterOptions.sources} types={filterOptions.infoCardTypeOptions}/>) : (<Segment>
					<Placeholder line={3}/>
				</Segment>)}
			{filtersMeta !== null ? (<InfoCardTypeFilter data-testid='loading' filter={filter} filteredSource={filteredSource !== null && filteredSource !== void 0 ? filteredSource : null} types={filterOptions.infoCardTypeOptions}/>) : (<Segment>
					<Placeholder line={7}/>
				</Segment>)}
			{filtersMeta !== null ? <TagsFilter extraTags={extraTags} filter={filter}/> : null}
			{filtersMeta !== null ? (<DateFilter filter={filter} onUpdate={onUpdate}/>) : (<Segment>
					<Placeholder line={3}/>
				</Segment>)}
		</Segment.Group>);
});
var parseFilter = function (searchParams) {
    var conditions = searchParams.getAll('filter').reduce(function (conditions, condition) {
        var _a = condition.split(':'), type = _a[0], values = _a[1];
        Array.prototype.push.apply(conditions, values.split(',').map(function (value) { return ({ type: type, value: value }); }));
        return conditions;
    }, []);
    return new InfoCardApi.Filter(conditions);
};
export var Dashboard = function (_a) {
    var _b, _c, _d;
    var _e = _a.infoCardId, infoCardId = _e === void 0 ? null : _e;
    var _f = useRequireAccessToken(), token = _f[1];
    var redirectToDashboard = DashboardRoute.useRedirect();
    var systemModules = useStore().systemModules;
    var allowBrandFilter = React.useMemo(function () { return systemModules.find(function (module) { return module === SystemModule.infoCardsFilterByBrands; }) !== undefined; }, [systemModules]);
    var searchParams = useSearchParams();
    var filter = React.useMemo(function () { return parseFilter(searchParams); }, [searchParams]);
    var updateFilter = React.useCallback(function (filter) {
        redirectToDashboard({ filter: filter });
    }, []);
    var _g = useApiResults(function (lastResponse) {
        var _a;
        return lastResponse === null || lastResponse.pagination.currentPage < lastResponse.pagination.totalPages
            ? new InfoCardApi.InfoCardsRequest(new ItemsQuery(((_a = lastResponse === null || lastResponse === void 0 ? void 0 : lastResponse.pagination.currentPage) !== null && _a !== void 0 ? _a : 0) + 1, ItemsPerPage, []), filter)
            : null;
    }, [token, filter]), results = _g[0], loadNext = _g[1], reloadInfoCards = _g[2];
    var _h = useApiResult(new InfoCardApi.FilterOptionsRequest(), []), filterOptions = _h[0], reloadFilterOptions = _h[1];
    var cardLink = React.useCallback(function (props) { return <InfoCardRoute.Link {...props} filter={filter}/>; }, [filter]);
    var cardsLink = React.useCallback(function (props) { return <DashboardRoute.Link {...props} filter={filter.added(props.tag)}/>; }, [filter]);
    var reload = React.useCallback(function () {
        var currentPage = results.value !== null ? results.value.length : 1;
        reloadInfoCards(currentPage);
        reloadFilterOptions();
    }, [results.value]);
    return (<Container>
			{infoCardId === null ? <ErrorConsumer>{function (error) { return <ErrorAlert>{error}</ErrorAlert>; }}</ErrorConsumer> : null}

			<Impersonate.Panel />

			{filterOptions.value !== null ? (<Filter allowBrandFilter={allowBrandFilter} filter={filter} filterOptions={filterOptions.value} filtersMeta={(_d = (_c = (_b = results.value) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.filtersMeta) !== null && _d !== void 0 ? _d : null} onUpdate={updateFilter}/>) : null}

			<Grid InfoCardLink={cardLink} InfoCardsLink={cardsLink} results={results} onLoad={loadNext} onReload={reload}/>

			{infoCardId !== null ? (<InfoCardModal id={infoCardId} InfoCardsLink={cardsLink} results={results} onClose={function () {
                setTimeout(function () {
                    redirectToDashboard({ filter: filter });
                }, 0);
            }}/>) : null}
		</Container>);
};
Dashboard.displayName = 'Application.Dashboard';
